import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Check, ChevronDown } from "lucide-react";

const LANGS = [
  { code: "nl", label: "Nederlands", short: "NL" },
  { code: "en", label: "English", short: "EN" },
  { code: "de", label: "Deutsch", short: "DE" },
  { code: "fr", label: "Français", short: "FR" },
  { code: "es", label: "Español", short: "ES" },
  { code: "it", label: "Italiano", short: "IT" },
  { code: "pt", label: "Português", short: "PT" },
  { code: "pl", label: "Polski", short: "PL" },
  { code: "tr", label: "Türkçe", short: "TR" },
];

export const LanguageSwitch = ({ variant = "header" }) => {
  const { i18n } = useTranslation();
  const wrapRef = useRef(null);
  const [open, setOpen] = useState(false);

  const code = (i18n.language || "nl").slice(0, 2).toLowerCase();
  const current = LANGS.find((l) => l.code === code) || LANGS[0];

  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const pickLang = (lng) => {
    i18n.changeLanguage(lng);
    setOpen(false);
  };

  return (
    <div ref={wrapRef} className="relative" data-testid="lang-switch-wrap">
      <button
        onClick={() => setOpen((o) => !o)}
        className={
          variant === "header"
            ? "flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-200 hover:border-[#1A2B4A] text-xs font-bold text-[#1A2B4A] uppercase transition-colors"
            : "flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-slate-50 text-sm font-medium text-[#1A2B4A]"
        }
        data-testid="lang-switch"
        aria-label="Switch language"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <Globe size={14} />
        <span>{current.short}</span>
        <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-44 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden z-50 py-1"
          role="listbox"
          data-testid="lang-switch-menu"
        >
          {LANGS.map((l) => {
            const active = l.code === current.code;
            return (
              <button
                key={l.code}
                role="option"
                aria-selected={active}
                onClick={() => pickLang(l.code)}
                className={`w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-amber-50/60 transition-colors ${
                  active ? "text-[#1A2B4A] font-semibold" : "text-slate-700"
                }`}
                data-testid={`lang-option-${l.code}`}
              >
                <span className="flex items-center gap-2">
                  <span className="text-[10px] font-bold uppercase text-slate-400 w-6">{l.short}</span>
                  {l.label}
                </span>
                {active && <Check size={14} className="text-[#F5A623]" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
